import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Translation strings
const translations = {
    en: {
        // Navigation
        home: 'Home',
        transactions: 'Transactions',
        khata: 'Khata',
        reports: 'Reports',
        settings: 'Settings',
        
        // Auth
        login: 'Login',
        enterPhone: 'Enter your phone number',
        sendOTP: 'Send OTP',
        enterOTP: 'Enter OTP',
        verifyOTP: 'Verify OTP',
        resendOTP: 'Resend OTP',
        phoneHint: 'We\'ll send you a 6-digit code',
        
        // Dashboard
        todaySales: 'Today\'s Sales',
        todayExpenses: 'Today\'s Expenses',
        todayProfit: 'Today\'s Profit',
        recentTransactions: 'Recent Transactions',
        highRiskAlerts: 'High Risk Alerts',
        pendingDues: 'Pending Dues',
        viewAll: 'View All',
        
        // Transactions
        addSale: 'Add Sale',
        addExpense: 'Add Expense',
        amount: 'Amount',
        category: 'Category',
        description: 'Description',
        save: 'Save',
        cancel: 'Cancel',
        sale: 'Sale',
        expense: 'Expense',
        
        // Risk
        riskScore: 'Risk Score',
        lowRisk: 'Low Risk',
        mediumRisk: 'Medium Risk',
        highRisk: 'High Risk',
        acceptAnyway: 'Accept Anyway',
        rejectFlag: 'Reject & Flag',
        reportFraud: 'Report to 1930',
        
        // Khata
        addCredit: 'Add Credit',
        customerName: 'Customer Name',
        amountDue: 'Amount Due',
        dueDate: 'Due Date',
        markPaid: 'Mark as Paid',
        sendReminder: 'Send Reminder',
        totalDue: 'Total Due',
        
        // Reports
        dailyReport: 'Daily Report',
        monthlyReport: 'Monthly Report',
        shareWhatsApp: 'Share on WhatsApp',
        downloadPDF: 'Download PDF',
        profit: 'Profit',
        loss: 'Loss',
        
        // Settings
        darkMode: 'Dark Mode',
        language: 'Language',
        notifications: 'Notifications',
        businessName: 'Business Name',
        ownerName: 'Owner Name',
        subscription: 'Subscription',
        upgrade: 'Upgrade',
        logout: 'Logout',
        
        // Subscription
        freePlan: 'Free Plan',
        proPlan: 'Pro Plan',
        businessPlan: 'Business Plan',
        perMonth: '/month',
        currentPlan: 'Current Plan',
        
        // Voice
        voiceEntry: 'Voice Entry',
        tapToSpeak: 'Tap to speak',
        listening: 'Listening...',
        
        // General
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        confirm: 'Confirm',
        delete: 'Delete',
        edit: 'Edit',
        
        // Disclaimer
        disclaimer: 'Advisory AI tool only. We do not hold funds or guarantee anything. Always verify yourself.',
    },
    hi: {
        // Navigation
        home: 'होम',
        transactions: 'लेन-देन',
        khata: 'खाता',
        reports: 'रिपोर्ट',
        settings: 'सेटिंग्स',
        
        // Auth
        login: 'लॉगिन',
        enterPhone: 'अपना फोन नंबर दर्ज करें',
        sendOTP: 'OTP भेजें',
        enterOTP: 'OTP दर्ज करें',
        verifyOTP: 'OTP सत्यापित करें',
        resendOTP: 'OTP पुनः भेजें',
        phoneHint: 'हम आपको 6-अंकों का कोड भेजेंगे',
        
        // Dashboard
        todaySales: 'आज की बिक्री',
        todayExpenses: 'आज के खर्च',
        todayProfit: 'आज का मुनाफा',
        recentTransactions: 'हाल के लेन-देन',
        highRiskAlerts: 'उच्च जोखिम अलर्ट',
        pendingDues: 'बकाया राशि',
        viewAll: 'सभी देखें',
        
        // Transactions
        addSale: 'बिक्री जोड़ें',
        addExpense: 'खर्च जोड़ें',
        amount: 'राशि',
        category: 'श्रेणी',
        description: 'विवरण',
        save: 'सहेजें',
        cancel: 'रद्द करें',
        sale: 'बिक्री',
        expense: 'खर्च',
        
        // Risk
        riskScore: 'जोखिम स्कोर',
        lowRisk: 'कम जोखिम',
        mediumRisk: 'मध्यम जोखिम',
        highRisk: 'उच्च जोखिम',
        acceptAnyway: 'फिर भी स्वीकार करें',
        rejectFlag: 'अस्वीकार और फ्लैग करें',
        reportFraud: '1930 पर रिपोर्ट करें',
        
        // Khata
        addCredit: 'उधार जोड़ें',
        customerName: 'ग्राहक का नाम',
        amountDue: 'बकाया राशि',
        dueDate: 'भुगतान तिथि',
        markPaid: 'भुगतान हुआ',
        sendReminder: 'रिमाइंडर भेजें',
        totalDue: 'कुल बकाया',
        
        // Reports
        dailyReport: 'दैनिक रिपोर्ट',
        monthlyReport: 'मासिक रिपोर्ट',
        shareWhatsApp: 'WhatsApp पर शेयर करें',
        downloadPDF: 'PDF डाउनलोड करें',
        profit: 'मुनाफा',
        loss: 'नुकसान',
        
        // Settings
        darkMode: 'डार्क मोड',
        language: 'भाषा',
        notifications: 'सूचनाएं',
        businessName: 'व्यापार का नाम',
        ownerName: 'मालिक का नाम',
        subscription: 'सदस्यता',
        upgrade: 'अपग्रेड करें',
        logout: 'लॉगआउट',
        
        // Subscription
        freePlan: 'फ्री प्लान',
        proPlan: 'प्रो प्लान',
        businessPlan: 'बिज़नेस प्लान',
        perMonth: '/महीना',
        currentPlan: 'वर्तमान प्लान',
        
        // Voice
        voiceEntry: 'आवाज़ से एंट्री',
        tapToSpeak: 'बोलने के लिए टैप करें',
        listening: 'सुन रहे हैं...',
        
        // General
        loading: 'लोड हो रहा है...',
        error: 'त्रुटि',
        success: 'सफल',
        confirm: 'पुष्टि करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        
        // Disclaimer
        disclaimer: 'यह केवल सलाहकार AI टूल है। हम फंड नहीं रखते और कुछ भी गारंटी नहीं देते। हमेशा खुद सत्यापित करें।',
    }
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('shieldpay_language') || 'en';
    });

    useEffect(() => {
        localStorage.setItem('shieldpay_language', language);
        document.documentElement.lang = language;
    }, [language]);

    const t = (key) => {
        return translations[language]?.[key] || translations.en[key] || key;
    };

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'hi' : 'en');
    };

    const value = {
        language,
        setLanguage,
        toggleLanguage,
        t,
        isHindi: language === 'hi'
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
